<template>
  <v-layout wrap>
    <v-flex xs12>
      <v-card>
        <Subheader v-model="search" search="guidelines" :title="$t('AnalyteGuidelinesManagement.tableName')">
          <v-btn color="success" dark @click="clickAddGuideline()">
            <v-icon class="mr-1">add</v-icon>{{ $t('Button.AddToGuideline') }}
          </v-btn>
        </Subheader>
        <v-card-text style="display: flex;" class="align-center">
          <v-icon class="mr-2" color="primary">info</v-icon>
          The guideline standards that this analyte has been used in.
        </v-card-text>
        <v-divider />
        <v-alert v-if="allMismatchedUnitGroup.length" :value="true" class="ma-0" color="warning lighten-2" icon="warning" outline>
          <span class="primary--text py-2">
            <span class="font-weight-bold">
              <span>
                {{ allMismatchedUnitGroup.length == 1 ? $t('AnalyteGuidelinesManagement.singleMismatchedUnitGroup') : $t('AnalyteGuidelinesManagement.multipleMismatchedUnitGroups') }}
              </span>
            </span><br>
            <span v-for="i in allMismatchedUnitGroup" :key="i.Guideline">
              <span class="font-weight-bold">Guideline: </span>{{i.Guideline}} {{i.GuidelineAnalyteName}} 
              <span class="font-weight-bold">Guideline Unit Group: </span>{{i.UnitGroup}}<br>
            </span>
          </span>
        </v-alert>
        <v-data-table :headers="headers" :items="analyteGuidelines" hide-actions>
          <template slot="items" slot-scope="props">
            <tr :class="getRowClass(props.item)">
              <td class="text-xs-left">
                <router-link :to="{ name: 'tenantGuidelinesDetail', params: { name: props.item.GuidelineName, } }">{{ props.item.GuidelineName }}</router-link>
              </td>
              <td class="text-xs-left">{{ props.item.AnalyteNameInGuideline }}</td>
              <td class="text-xs-left">{{ props.item.ValueType }}</td>
              <td class="text-xs-left" v-html="getGuidelineValue(props.item)"></td>
              <td class="text-xs-left">{{ props.item.Unit }}</td>
              <td class="text-xs-right">
                <v-btn small @click="clickDeleteGuideline(props.item)">
                  <v-icon class="mr-1" color="error">delete_forever</v-icon>{{ $t('Button.Remove') }}
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>

    <v-dialog v-model="addDialog" persistent max-width="800px">
      <v-card>
        <v-toolbar dense dark color="primary">
          <v-toolbar-title>{{ titleInfo }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="addDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-form v-model="valid" ref="form" lazy-validation style="width: 100%">
          <v-card-text>
            <v-alert v-if="!selectGuidelines.length || !analyteData.ApplicableUnitGroupId" outline type="warning" v-model="alertModel">{{ alertMsg }}</v-alert>
            <div v-else>
              <v-autocomplete v-model="newGuideline.guideline" :items="selectGuidelines" :label="$t('AnalyteGuidelinesManagement.dialogInput1')" item-text="GuidelineName" return-object :rules="guidelineRule" @change="changeGuideline" persistent-hint :hint="guidelineHint" />
              <v-autocomplete v-model="newGuideline.standard" :items="selectAnalyte" :label="$t('AnalyteGuidelinesManagement.dialogInput2')" item-text="AnalyteNameInGuideline" return-object :rules="standardRule" />
              <div class="subheading" v-if="methodAnalyte != null">
                <div v-if="!isAmmonialookuptable(newGuideline.standard.ValueType)"><b>Guideline Value:
                  </b>{{ newGuideline.standard.StandardValue }}</div>
                <div v-if="isAmmonialookuptable(newGuideline.standard.ValueType)"><b>Guideline Value: </b>
                  <div v-html="getGuidelineValueLong(newGuideline.standard.StandardValue)"></div>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" @click="addGuidelineStandard()" v-if="selectGuidelines.length && analyteData.ApplicableUnitGroupId">
              <v-icon class="mr-1">done</v-icon>{{ $t('Button.Save') }}
            </v-btn>
            <v-btn @click="closeDialog()">{{ $t('Button.Cancel') }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDialog" persistent max-width="800">
      <v-card>
        <v-toolbar dense dark color="primary">
          <v-toolbar-title>Unlink from guideline standard</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="confirmDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          {{ $t('AnalyteGuidelinesManagement.deleteTitleMsg', { msg: deleteAnalyteStandardMap.AnalyteNameInGuideline }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="delete" dark @click.native="deleteGuidelineStandard()">
            <v-icon class="mr-1">delete_forever</v-icon>{{ $t('Button.unlink') }}
          </v-btn>
          <v-btn @click.native="confirmDialog = false">{{ $t('Button.Cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <v-snackbar :color="snackColor" :timeout="null" top v-model="snackbar" auto-height>
        {{ snackMessage }}
        <v-btn dark flat @click.native="snackbar = false">{{ $t('Button.Close') }}</v-btn>
      </v-snackbar>
    </v-card>
  </v-layout>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'
  import {
    pluralize,
  } from '@/helpers/strings'
  import { errorMessage } from '@/helpers/exceptionHandling'
  import { AmmoniaLookupTable, LookupTable } from '@/components/mixins/GuidelinesManageUtil'
  import { checkUnitNamesEqual } from '@/store/unit'
  const guidelines = createNamespacedHelpers('guidelines')
  const analyte = createNamespacedHelpers('analyte')
  const authenticate = createNamespacedHelpers('authenticate')
  const unit = createNamespacedHelpers('unit')

  import Subheader from '@/components/generic/Subheader'

  export default {
    components: {
      Subheader,
    },
    props: {
      analyteData: {
        type: Object,
        default: () => {
          return {
            AnalyteName: '',
            AnalyteStandardMaps: [],
          }
        },
      },
    },
    data() {
      return {
        addDialog: false,
        confirmDialog: false,
        titleInfo: '',
        alertModel: true,
        valid: true,
        search: '',
        snackbar: false,
        snackColor: '',
        snackMessage: '',
        editGuideline: {},
        standardData: {},
        deleteAnalyteStandardMap: {},
        guidelineRule: [
          (v) => !!v || 'Guideline is required',
        ],
        standardRule: [
          (v) => !!v || 'Anaylte is required',
        ],
        headers: [{
          text: this.$t('AnalyteGuidelinesManagement.tableHeader1'),
          align: 'left',
          value: 'GuidelineName',
        },
        {
          text: this.$t('AnalyteGuidelinesManagement.tableHeader2'),
          align: 'left',
          value: 'AnalyteNameInGuideline',
        },
        {
          text: 'Value Type',
          align: 'left',
          value: 'ValueType',
        },
        {
          text: this.$t('AnalyteGuidelinesManagement.tableHeader3'),
          align: 'left',
          value: 'StandardValue',
        },
        {
          text: 'Unit',
          align: 'left',
          value: 'Unit',
        },
        {
          text: this.$t('AnalyteGuidelinesManagement.tableHeader7'),
          value: '',
          align: 'center',
        },
        ],
        pagination: {
          sortBy: 'GuidelineName',
          rowsPerPage: 10,
        },
        newGuideline: {
          guideline: {},
          standard: {},
        },
      }
    },
    mounted() {},
    computed: {
      ...guidelines.mapGetters(['Guidelines']),
      ...analyte.mapGetters(['tenantAnalyteList']),
      ...authenticate.mapGetters(['currentUserGroup']),
      ...unit.mapGetters(['allUnits', 'allUnitGroups']),
      guidelineHint() {
        if (this.analyteData.ApplicableUnitGroupId && this.allUnitGroups.length) {
          return `Only guidelines that have standards the current analyte unit group ${this.allUnitGroups.find(x => x.Id == this.analyteData.ApplicableUnitGroupId).Name} are shown`
        }
      },
      alertMsg() {
        if (this.analyteData.ApplicableUnitGroupId && this.allUnitGroups.length) {
          return `There is no guideline contain standards for unit group ${this.allUnitGroups.find(x => x.Id == this.analyteData.ApplicableUnitGroupId).Name}.`
        } else {
          return 'The applicable unit group is not defined for the analyte, please assign applicable unit group for the analyte before configuring the guideline.'
        }
      },
      selectGuidelines() {
        let matched = []
        if (this.analyteData.ApplicableUnitGroupId && this.allUnitGroups.length) {
          var data = JSON.parse(JSON.stringify(this.Guidelines))
          matched = data.filter(x => x.TenantGuidelines.some(t => t.TenantId == this.currentUserGroup.id) && this.analyteGuidelines.every(y => y.GuidelineId != x.Id))
          let targetUnits = this.allUnitGroups.find(x => x.Id == this.analyteData.ApplicableUnitGroupId).Units.map(x => x.Name)
          matched = matched.filter(x => {
            if (targetUnits.find(y => x.Standards.some(z => checkUnitNamesEqual(y, z.Unit)))) {
              return x
            }
          })
        }
        return matched
      },
      selectAnalyte() {
        if (this.newGuideline.guideline.Standards) {
          var data = this.newGuideline.guideline.Standards
          var thresholdOfAnalyte = this.analyteGuidelines
          var matched = data.filter(x =>
            thresholdOfAnalyte.every(
              y => ((y.Id != x.Id))
            )
          )
          if (this.analyteData.ApplicableUnitGroupId) {
            let targetUnits = this.allUnitGroups.find(x => x.Id == this.analyteData.ApplicableUnitGroupId).Units.map(x => x.Name)
            matched = matched.filter(x => {
              if (targetUnits.find(y => checkUnitNamesEqual(y, x.Unit))) {
                return x
              }
            })
          }
          return matched
        }
      },
      methodAnalyte() {
        var reg = /\[(.+?)\]/g
        this.newGuideline.methodAnalyte = []
        if (this.newGuideline.standard.StandardValue) {
          var matched = this.newGuideline.standard.StandardValue.match(reg)
          var result = [...new Set(matched)]
          result.forEach(element => {
            let text = element.match(/\[(.+)\]/)
            element = text[1]
            if (element != 'Math.E') {
              this.newGuideline.methodAnalyte.push({
                AnalyteName: element,
              })
            }
          })
          return result
        }
        return null
      },
      analyteGuidelines() {
        if (!this.Guidelines || !this.Guidelines.length) {
          return []
        }
        let arr = []
        let matched = this.analyteData.AnalyteStandardMaps
        if (!matched || !matched.length) {
          return []
        }
        matched.map(element => {
          let guideline = this.Guidelines.find(x => x.Id == element.GuidelineId)
          let standard = guideline.Standards.find(x => x.Id == element.StandardId)
          arr.push({
            Id: element.Id,
            GuidelineId: standard.GuidelineId,
            GuidelineName: guideline.GuidelineName,
            Category: guideline.Category,
            StandardId: standard.Id,
            AnalyteNameInGuideline: standard.AnalyteNameInGuideline,
            Unit: standard.Unit,
            StandardValue: standard.StandardValue,
            ValueType: standard.ValueType,
            ReferenceSource: standard.ReferenceSource,
            ComparisonOperation: standard.ComparisonOperation,
          })
        })
        return arr
      },
      allMismatchedUnitGroup() {
        let arr = []
        this.analyteGuidelines.forEach(guideline => {
          let unit = this.allUnits.find(x => checkUnitNamesEqual(x.Name, guideline.Unit))
          let analyteUnitGroupName = this.allUnitGroups.find(x => x.Id == this.analyteData.ApplicableUnitGroupId).Name
          let matched = unit.Groups.some(x => x.Name.toLowerCase().replace(/\s/g, '') ==  analyteUnitGroupName.toLowerCase().replace(/\s/g, ''))
          if (!matched) {
            let group = unit.Groups.map(x => x.Name).join(',')
            arr.push({
              Guideline: guideline.GuidelineName,
              GuidelineAnalyteName: guideline.AnalyteNameInGuideline,
              UnitGroup: group,
            })
          }
        })
        return arr
      },
    },
    watch: {
      analyteGuidelines(v) {
        this.$emit('setAnalyteGuidelines', v)
      },
    },
    methods: {
      ...guidelines.mapActions(['postAddGuidelineStandard',
        'postDeleteGuidelineStandard',
      ]),
      ...analyte.mapActions(['addAnalyteGuideline', 'deleteAnalyteGuideline']),
      pluralize,
      changeGuideline() {
        this.newGuideline.standard = {}
      },
      startSnack(snackColor, snackMessage) {
        this.snackMessage = snackMessage
        this.snackColor = snackColor
        this.snackbar = true
      },
      isAmmonialookuptable(data) {
        return data == AmmoniaLookupTable
      },
      ammonialookuptableList() {
        this.newGuideline.methodAnalyte = [{
          AnalyteName: 'PH',
        }, {
          AnalyteName: 'Temp',
        }]
        return this.newGuideline.methodAnalyte
      },
      getGuidelineValue(guideline) {
        var result = null
        if(guideline.ValueType == AmmoniaLookupTable) {
          return JSON.parse(guideline.StandardValue).Description
        } else if(guideline.ValueType == LookupTable) {
          return '<b class="warning">Viewing lookup table guideline value is not supported yet.</b>'
        } else {
          result = guideline.StandardValue.replace('-99999', 'None')
        }
        if (guideline.ValueType == 'equation') {
          var reg = /\[(.+?)\]/g
          var matched = guideline.StandardValue.match(reg)
          matched.forEach(element => {
            guideline.StandardValue.replace(element, 'None')
          })
          result = guideline.StandardValue
        }
        return result
      },
      getGuidelineValueLong(guideline) {
        var result = JSON.parse(guideline).Description
        result = result.split('\'')
        var loop
        var link = ''
        var toLink
        for (loop = 0; loop < result.length; loop++) {
          if (result[loop].startsWith('http')) {
            link = result[loop]
            toLink = '<a target="#" href=' + link + '>' + link + '</a>'
            return toLink
          }
        }
        return false
      },
      clickAddGuideline() {
        this.newGuideline = {
          guideline: {},
          standard: {},
        }
        this.titleInfo = this.$t('AnalyteGuidelinesManagement.addTitleMsg')
        this.addDialog = true
      },
      clickDeleteGuideline(data) {
        this.deleteAnalyteStandardMap = data
        this.confirmDialog = true
      },
      closeDialog() {
        this.addDialog = false
      },
      addGuidelineStandard() {
        if (this.newGuideline.standard.GuidelineId && this.newGuideline.standard.Id) {
          let options = {
            data: {
              Id: null,
              AnalyteId: this.analyteData.Id,
              GuidelineId: this.newGuideline.standard.GuidelineId,
              StandardId: this.newGuideline.standard.Id,
            },
            successCb: () => {
              this.closeDialog()
              this.startSnack('success', this.$t('AnalyteGuidelinesManagement.successSaveMsg'))
            },
            failedCb: (err) => {
              this.startSnack('error', errorMessage(err))
            },
          }
          this.addAnalyteGuideline(options)
        } else {
          this.startSnack('error', this.$t('AnalyteGuidelinesManagement.nullMsg'))
        }
      },
      deleteGuidelineStandard() {
        let options = {
          data: {
            AnalyteId: this.analyteData.Id,
            StandardId: this.deleteAnalyteStandardMap.StandardId,
          },
          successCb: () => {
            this.confirmDialog = false
            this.startSnack('success', this.$t('AnalyteGuidelinesManagement.successDeleteMsg'))
          },
          failedCb: (err) => {
            this.startSnack('error', errorMessage(err))
          },
        }
        this.deleteAnalyteGuideline(options)
      },
      getRowClass(guideline) {   
        let targetUnits = this.allUnitGroups.find(x => x.Id == this.analyteData.ApplicableUnitGroupId).Units.map(x => x.Name)
        let matched = targetUnits.findIndex(x => checkUnitNamesEqual(x, guideline.Unit))        
        return matched == -1 ? 'warning-component' : ''
      },
    },
  }
</script>

<style scoped>
</style>